/**
 * Pure presentation helpers for `/inbox` — the matter stage vocabulary, the
 * urgency tone and the timestamp strip. Sibling of `pr-board.ts`, and split out
 * of the page for the same reason: the web tree has no DOM harness, so anything
 * with a branch in it has to live where a unit test can reach it.
 *
 * `inboxStageLabel` used to be exported from `pages/Inbox.tsx` and imported by
 * `pages/InboxThreadDetail.tsx` — a page reaching into a page. Same function,
 * proper home.
 */

import type { InboxThreadStage } from "./api";

const STAGE_LABELS: Readonly<Record<InboxThreadStage, string>> = {
  unclassified: "Unclassified",
  triaged: "Triaged",
  drafting: "Drafting",
  awaiting_mario: "Awaiting you",
  revising: "Revising",
  replied: "Replied",
  awaiting_them: "Awaiting them",
  needs_mario: "Needs you",
  closed: "Closed",
};

/** Human label for a matter stage (falls back to the raw enum value). */
export function inboxStageLabel(stage: string): string {
  return STAGE_LABELS[stage as InboxThreadStage] ?? stage;
}

/**
 * Tag class for an urgency. `normal` is deliberately styled like `low` here —
 * the page never renders a chip for it, so the tone only matters when the
 * classifier has said something worth reading.
 */
export function urgencyTone(urgency: string | null): string {
  if (urgency === "high") return "tag-error";
  if (urgency === "low") return "";
  return "tag-success";
}

// ---------------------------------------------------------------------------
// 2026-08-15 (inbox-pr-simplify) — the board shows THREE BUCKETS, not the
// nine-stage machine. The stages stay untouched under the hood (they drive
// the draft → approval → revise loop); what died is the pretence that the
// operator's own mail is a pipeline. A matter answers exactly one question on
// the board: does it need Mario, is it being handled, or is it done. The
// stamp strip (`inboxProgressSteps`) was deleted in the same slice — the
// timeline on the detail page is the record.
// ---------------------------------------------------------------------------

export type InboxBucket = "needs_you" | "in_motion" | "closed";

const BUCKET_FOR_STAGE: Readonly<Record<InboxThreadStage, InboxBucket>> = {
  unclassified: "in_motion",
  triaged: "in_motion",
  drafting: "in_motion",
  awaiting_mario: "needs_you",
  revising: "in_motion",
  replied: "in_motion",
  awaiting_them: "in_motion",
  needs_mario: "needs_you",
  closed: "closed",
};

export const INBOX_BUCKET_LABELS: Readonly<Record<InboxBucket, string>> = {
  needs_you: "Needs you",
  in_motion: "Being handled",
  closed: "Closed",
};

/** Board order: what needs Mario first, live work second, history last. */
export const INBOX_BUCKET_ORDER: readonly InboxBucket[] = [
  "needs_you",
  "in_motion",
  "closed",
];

/** Bucket for a stage; unknown values fail safe into `in_motion`. */
export function inboxBucketForStage(stage: string): InboxBucket {
  return BUCKET_FOR_STAGE[stage as InboxThreadStage] ?? "in_motion";
}

/**
 * Regroup the server's per-stage groups into the three buckets, preserving
 * the server's within-group ordering (stage pipeline order, newest first
 * within a stage — so inside "Being handled" the earlier-pipeline matters
 * still lead). Empty buckets are omitted.
 */
export function bucketInboxGroups<T>(
  groups: ReadonlyArray<{
    readonly stage: string;
    readonly threads: ReadonlyArray<T>;
  }>,
): ReadonlyArray<{ readonly bucket: InboxBucket; readonly threads: readonly T[] }> {
  const byBucket = new Map<InboxBucket, T[]>();
  for (const g of groups) {
    const bucket = inboxBucketForStage(g.stage);
    const list = byBucket.get(bucket) ?? [];
    list.push(...g.threads);
    byBucket.set(bucket, list);
  }
  return INBOX_BUCKET_ORDER.flatMap((bucket) => {
    const threads = byBucket.get(bucket);
    return threads !== undefined && threads.length > 0
      ? [{ bucket, threads }]
      : [];
  });
}
